import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, withCtx as _withCtx, renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = ["innerHTML"]

import { ref, onMounted } from "vue";
import { Status } from "@/validation";
import { required, min } from "@/validation/rules";
import { useI18n } from "vue3-i18n";
import FormInput from "@/components/forms/FormInput.vue";
import FormSearchableSelect from "@/components/forms/FormSearchableSelect.vue";
import Fieldset from "@/components/forms/Fieldset.vue";
import FormCheckbox from "@/components/forms/FormCheckbox.vue";
import FormRow from "@/components/forms/FormRow.vue";
import FormFiles from "@/components/forms/FormFiles.vue";
import {Product} from "@/types/entities/product";
import FormTextEditor from "@/components/forms/FormTextEditor.vue";
import FormTooltip from "@/components/forms/FormTooltip.vue";
import AddItemCodePopup from "@/components/products/AddItemCodePopup.vue";
import AddAccountCodePopup from "@/components/products/AddAccountCodePopup.vue";
import {FormOption} from "@/types/formOption";
import {useAccountingSoftwareCodeService} from "@/inversify.config";


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductBaseForm',
  props: {
    product: {},
    title: {},
    type: {},
    nameFrLabel: {},
    nameEnLabel: {},
    descriptionFrLabel: {},
    descriptionEnLabel: {}
  },
  emits: ["handleValidation", "registerFormInputRef"],
  setup(__props: any, { emit: __emit }) {

const { t } = useI18n();
const accountingSoftwareCodeService = useAccountingSoftwareCodeService();

const itemCodeOptions = ref<FormOption[]>([]);
const accountCodeOptions = ref<FormOption[]>([]);
const showAddItemCodePopup = ref(false);
const showAddAccountCodePopup = ref(false);

onMounted(async () => {
  const { itemCodes, accountCodes } = await accountingSoftwareCodeService.getAccountingSoftwareCodes(props.type);

  itemCodeOptions.value = itemCodes.map(code => ({
    name: code.value,
    label: code.value
  }));

  accountCodeOptions.value = accountCodes.map(code => ({
    name: code.value,
    label: `${code.value} - ${code.name}`
  }));
});

// eslint-disable-next-line no-undef
const props = __props

// eslint-disable-next-line
const emit = __emit

const product = ref<Product>(props.product)

function addFormInputRef(ref: typeof FormInput) {
    emit("registerFormInputRef", ref);
}

async function onImageChange(event: any, type: string) {
  const file = event?.target?.files[0];
  if (file && type === "card") product.value.cardImage = file as File
  if (file && type === "cardEn") product.value.cardImageEn = file as File
}

async function onSavedImageChange(url: string, type: string) {
  if (type === "card") product.value.savedCardImage = url
  if (type === "cardEn") product.value.savedCardImageEn = url
}

async function handleValidation(name: string, validationStatus: Status) {
    emit("handleValidation", name, validationStatus);
}

function onItemCodeCreated(code: { value: string }) {
  const option = { name: code.value, label: code.value };
  itemCodeOptions.value.push(option);
  product.value.itemCode = code.value;
}

function onAccountCodeCreated(code: { value: string, name: string }) {
  const option = { name: code.value, label: `${code.value} - ${code.name}` };
  accountCodeOptions.value.push(option);
  product.value.accountCode = code.value;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(Fieldset, {
      title: props.title
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormFiles, {
              modelValue: product.value.cardImage,
              "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((product.value.cardImage) = $event)),
              name: "cardImage",
              ref: addFormInputRef,
              label: _unref(t)('products.form.image-card'),
              tooltip: _unref(t)(`products.form.image-card-tooltip`),
              possiblesTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
              "default-selected-files": product.value.savedCardImage != null ? [product.value.savedCardImage] : [],
              "can-delete": "",
              onOnDefaultSelectedFilesChange: _cache[1] || (_cache[1] = ($event: any) => (onSavedImageChange($event, 'card'))),
              onChange: _cache[2] || (_cache[2] = ($event: any) => (onImageChange($event, 'card'))),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "tooltip", "default-selected-files"]),
            _createVNode(FormFiles, {
              modelValue: product.value.cardImageEn,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((product.value.cardImageEn) = $event)),
              name: "cardImageEn",
              ref: addFormInputRef,
              label: _unref(t)('products.form.image-card-en'),
              tooltip: _unref(t)(`products.form.image-card-tooltip`),
              possiblesTypes: ['image/png', 'image/jpg', 'image/jpeg', 'image/webp'],
              "default-selected-files": product.value.savedCardImageEn != null ? [product.value.savedCardImageEn] : [],
              "can-delete": "",
              onOnDefaultSelectedFilesChange: _cache[4] || (_cache[4] = ($event: any) => (onSavedImageChange($event, 'cardEn'))),
              onChange: _cache[5] || (_cache[5] = ($event: any) => (onImageChange($event, 'cardEn'))),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "tooltip", "default-selected-files"])
          ]),
          _: 1
        }),
        _renderSlot(_ctx.$slots, "image"),
        _renderSlot(_ctx.$slots, "after-images"),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              modelValue: product.value.nameFr,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((product.value.nameFr) = $event)),
              type: "text",
              name: "name-fr",
              ref: addFormInputRef,
              label: _unref(t)(props.nameFrLabel ?? 'products.form.name-fr'),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"]),
            _createVNode(FormInput, {
              modelValue: product.value.nameEn,
              "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((product.value.nameEn) = $event)),
              type: "text",
              name: "name-en",
              ref: addFormInputRef,
              label: _unref(t)(props.nameEnLabel ?? 'products.form.name-en'),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label"])
          ]),
          _: 1
        }),
        _createVNode(FormTextEditor, {
          modelValue: product.value.descriptionFr,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((product.value.descriptionFr) = $event)),
          ref: addFormInputRef,
          name: "description-fr",
          label: _unref(t)(props.descriptionFrLabel ?? 'products.form.description-fr'),
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]),
        _createVNode(FormTextEditor, {
          modelValue: product.value.descriptionEn,
          "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((product.value.descriptionEn) = $event)),
          ref: addFormInputRef,
          name: "description-en",
          label: _unref(t)(props.descriptionEnLabel ?? 'products.form.description-en'),
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"]),
        _renderSlot(_ctx.$slots, "before-prices")
      ]),
      _: 3
    }, 8, ["title"]),
    _createVNode(Fieldset, {
      title: _unref(t)('products.form.price')
    }, {
      default: _withCtx(() => [
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createVNode(FormInput, {
              type: "number",
              name: "member-price",
              modelValue: product.value.memberPrice,
              "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((product.value.memberPrice) = $event)),
              modelModifiers: { number: true },
              ref: addFormInputRef,
              label: _unref(t)('products.form.memberPrice'),
              rules: [_unref(required), _unref(min)(0)],
              tooltip: _unref(t)('products.form.no-taxes'),
              onValidated: handleValidation
            }, null, 8, ["modelValue", "label", "rules", "tooltip"]),
            (!product.value.membersOnly)
              ? (_openBlock(), _createBlock(FormInput, {
                  key: 0,
                  type: "number",
                  name: "non-member-price",
                  modelValue: product.value.nonMemberPrice,
                  "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((product.value.nonMemberPrice) = $event)),
                  modelModifiers: { number: true },
                  ref: addFormInputRef,
                  label: _unref(t)('products.form.nonMemberPrice'),
                  rules: product.value.membersOnly ? [] : [_unref(required), _unref(min)(0)],
                  tooltip: _unref(t)('products.form.no-taxes'),
                  onValidated: handleValidation
                }, null, 8, ["modelValue", "label", "rules", "tooltip"]))
              : _createCommentVNode("", true)
          ]),
          _: 1
        }),
        _createVNode(FormCheckbox, {
          name: "membersOnly",
          modelValue: product.value.membersOnly,
          "onUpdate:modelValue": [
            _cache[12] || (_cache[12] = ($event: any) => ((product.value.membersOnly) = $event)),
            _cache[13] || (_cache[13] = newValue => {if (newValue) product.value.nonMemberPrice = undefined })
          ],
          ref: addFormInputRef,
          label: _unref(t)('products.form.membersOnly'),
          onValidated: handleValidation
        }, null, 8, ["modelValue", "label"])
      ]),
      _: 1
    }, 8, ["title"]),
    _createVNode(Fieldset, {
      title: _unref(t)('products.form.accountingSoftwareConfiguration')
    }, {
      default: _withCtx(() => [
        _createVNode(FormTooltip, null, {
          default: _withCtx(() => [
            _createElementVNode("p", {
              innerHTML: _unref(t)(`products.form.accountingSoftwareConfiguration.tooltip`)
            }, null, 8, _hoisted_1)
          ]),
          _: 1
        }),
        _createVNode(FormRow, null, {
          default: _withCtx(() => [
            _createElementVNode("div", null, [
              _createVNode(FormSearchableSelect, {
                name: "account-code-number",
                modelValue: product.value.accountCode,
                "onUpdate:modelValue": _cache[14] || (_cache[14] = ($event: any) => ((product.value.accountCode) = $event)),
                ref: addFormInputRef,
                label: _unref(t)('products.form.accountingSoftwareConfiguration.accountCode'),
                options: accountCodeOptions.value,
                rules: [],
                onValidated: handleValidation
              }, null, 8, ["modelValue", "label", "options"]),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn--add-code",
                onClick: _cache[15] || (_cache[15] = ($event: any) => (showAddAccountCodePopup.value = true))
              }, _toDisplayString(_unref(t)('global.add')), 1)
            ]),
            _createElementVNode("div", null, [
              _createVNode(FormSearchableSelect, {
                name: "item-code",
                modelValue: product.value.itemCode,
                "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((product.value.itemCode) = $event)),
                ref: addFormInputRef,
                label: _unref(t)('products.form.accountingSoftwareConfiguration.itemCode'),
                options: itemCodeOptions.value,
                rules: [],
                onValidated: handleValidation
              }, null, 8, ["modelValue", "label", "options"]),
              _createElementVNode("button", {
                type: "button",
                class: "btn btn--add-code",
                onClick: _cache[17] || (_cache[17] = ($event: any) => (showAddItemCodePopup.value = true))
              }, _toDisplayString(_unref(t)('global.add')), 1)
            ])
          ]),
          _: 1
        }),
        (showAddAccountCodePopup.value)
          ? (_openBlock(), _createBlock(AddAccountCodePopup, {
              key: 0,
              onClosePopup: _cache[18] || (_cache[18] = ($event: any) => (showAddAccountCodePopup.value = false)),
              onCodeCreated: onAccountCodeCreated
            }))
          : _createCommentVNode("", true),
        (showAddItemCodePopup.value)
          ? (_openBlock(), _createBlock(AddItemCodePopup, {
              key: 1,
              onClosePopup: _cache[19] || (_cache[19] = ($event: any) => (showAddItemCodePopup.value = false)),
              onCodeCreated: onItemCodeCreated
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["title"])
  ]))
}
}

})